import type { EventCheckout, OrganizationPublic } from '@irace/shared';
import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { COPY } from '../copy/vi.ts';
import { CheckoutPanel, type CheckoutState } from './CheckoutPanel.tsx';

/**
 * M07 — accepted (§17).
 *
 * The only green screen in the app, and it is reachable from exactly one
 * place: an HTTP 202 (AGENTS.md invariant 5). No Queue or database wording
 * appears here; from the participant's side the check-in is done.
 *
 * Organizer content belongs here and not in the form (decision 6): before
 * checking in, everything on screen should be about checking in. Afterwards
 * is when someone has a reason to look at who is running the event.
 */
export function CheckinAccepted({
  locationName,
  checkedInAt,
  organization,
  eventUrl,
  checkout,
  checkoutState,
  onCheckout,
  onCheckinAgain,
}: {
  /** Null when the event has no real location, so none was ever shown. */
  locationName: string | null;
  /**
   * When the check-in happened, for a screen restored on a later visit.
   *
   * Null right after the 202: someone who just watched the screen turn green
   * does not need to be told what time it is.
   */
  checkedInAt?: string | null;
  organization?: OrganizationPublic | null;
  eventUrl?: string | null;
  /** G-014 — present only while the event has check-out on. */
  checkout?: EventCheckout | null;
  checkoutState: CheckoutState;
  onCheckout: () => void;
  /**
   * The way back to a blank form, on a screen restored from an earlier visit.
   *
   * Not optional there and absent everywhere else. Accepted records live six
   * hours (§17), so a shared desk phone that reopens on this screen would be
   * stuck on the previous person for the rest of the event without it — while
   * a participant looking at their own fresh confirmation has no reason to be
   * offered a second check-in, and every reason not to tap one by accident.
   */
  onCheckinAgain?: (() => void) | null;
}) {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col justify-between bg-bg px-5 py-10">
      <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
        <div
          className="flex size-20 items-center justify-center rounded-full bg-success text-4xl text-white"
          aria-hidden="true"
        >
          ✓
        </div>
        <h1 className="text-2xl font-extrabold tracking-wide text-success">
          {COPY.accepted.title}
        </h1>
        <p className="text-base text-text-muted">{COPY.accepted.body}</p>

        {checkedInAt ? (
          <p className="text-base text-text-muted">
            {COPY.accepted.atLabel} {checkedInAt}
          </p>
        ) : null}

        {locationName ? (
          <div className="flex flex-col gap-1 pt-2">
            <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
              {COPY.accepted.locationLabel}
            </span>
            <span className="text-lg font-bold">{locationName}</span>
          </div>
        ) : null}

        {/* Above the organizer block on purpose: this is the one thing on the
            screen the participant still has to do, and it must not sit below a
            logo and a description. */}
        {checkout ? (
          <CheckoutPanel checkout={checkout} state={checkoutState} onCheckout={onCheckout} />
        ) : null}

        {organization ? (
          <div className="flex flex-col items-center gap-2 pt-6">
            <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
              {COPY.accepted.organizerHeading}
            </span>
            {organization.logo_url ? (
              // Bounded height: an organizer's upload must not push the
              // confirmation off the screen it exists to deliver.
              <img
                src={organization.logo_url}
                alt={organization.name}
                className="max-h-16 w-auto"
              />
            ) : null}
            <span className="text-base font-bold">{organization.name}</span>
            {organization.description ? (
              <p className="text-sm text-text-muted">{organization.description}</p>
            ) : null}
            {organization.website_url ? (
              <a
                href={organization.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-brand underline"
              >
                {organization.website_url.replace(/^https:\/\//, '')}
              </a>
            ) : null}
          </div>
        ) : null}

        {eventUrl ? (
          <a
            href={eventUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-brand underline"
          >
            {COPY.accepted.eventPageLink}
          </a>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        {onCheckinAgain ? (
          // A real button rather than a text link: on a shared desk phone this
          // is the control the next person in the queue depends on, and it is
          // competing with a full-height green confirmation for attention.
          <button
            type="button"
            onClick={onCheckinAgain}
            className="h-12 w-full rounded-control bg-brand px-5 text-base font-extrabold tracking-wide text-brand-contrast"
          >
            {COPY.accepted.checkinAgain}
          </button>
        ) : null}

        <PoweredByFooter organization={organization} />
      </div>
    </div>
  );
}
